import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

function NonTeachingStaffReport() {
  // ✅ Session (same as registration)
  const currentSessionId = localStorage.getItem('currentSessionId');

  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [designations, setDesignations] = useState([]);

  /* ---------------- FETCH DATA (MATCH REGISTRATION) ---------------- */
  useEffect(() => {
    if (!currentSessionId) return;

    const fetchData = async () => {
      try {
        const [staffRes, designationRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/nonTeachingStaff/getAllNonTeachingStaff`,
            { params: { sessionId: currentSessionId } }
          ),
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/allDesignations/getDesignations`,
            {
              params: {
                sessionId: currentSessionId,
                type: 'Non-Teacher',
              },
            }
          ),
        ]);

        setStaff(staffRes.data || []);
        setFilteredStaff(staffRes.data || []);
        setDesignations(designationRes.data || []);
      } catch (err) {
        console.error('Error fetching non-teaching staff:', err);
      }
    };

    fetchData();
  }, [currentSessionId]);

  /* ---------------- SEARCH & FILTER ---------------- */
  const handleSearch = () => {
    let data = staff;

    if (searchTerm) {
      data = data.filter((s) =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase())

      );
    }

    if (filterDesignation) {
  data = data.filter(
    (s) => s.designation?.designationName === filterDesignation
  );
}


    if (filterGender) {
      data = data.filter((s) => s.gender === filterGender);
    }

    setFilteredStaff(data);
  };

  /* ---------------- SUMMARY COUNTS ---------------- */
  const totalStaff = filteredStaff.length;

  const totalMale = useMemo(
    () => staff.filter((s) => s.gender === 'Male').length,
    [staff]
  );

  const totalFemale = useMemo(
    () => staff.filter((s) => s.gender === 'Female').length,
    [staff]
  );

  /* ---------------- EXPORT ---------------- */
  const exportToExcel = () => {
    const data = filteredStaff.map((s, i) => ({
      'Sr No': i + 1,
      Name: s.name,
     Designation: s.designation?.designationName,
      Gender: s.gender,
      Contact: s.contactNumber,
      Session: s.sessionId,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Non-Teaching Staff');
    XLSX.writeFile(wb, 'Non_Teaching_Staff_Report.xlsx');
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-100 p-4 rounded">
            <h4 className="text-blue-700 font-semibold">Total Staff</h4>
            <p className="text-2xl font-bold text-blue-800">{totalStaff}</p>
          </div>
          <div className="bg-green-100 p-4 rounded shadow">
            <h4 className="text-green-700 font-semibold">Male Staff</h4>
            <p className="text-2xl font-bold text-green-800">{totalMale}</p>
          </div>
          <div className="bg-pink-100 p-4 rounded shadow">
            <h4 className="text-pink-700 font-semibold">Female Staff</h4>
            <p className="text-2xl font-bold text-pink-800">{totalFemale}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Search by name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border rounded px-3 py-2"
            />

            <select
              value={filterDesignation}
              onChange={(e) => setFilterDesignation(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Designations</option>
              {designations.map((d) => (
                <option key={d._id} value={d.designationName}>
                  {d.designationName}
                </option>
              ))}
            </select>

            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Genders</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>

            <button
              onClick={handleSearch}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Staff List</h3>
            <button
              onClick={exportToExcel}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Export to Excel
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-4 py-2">Sr</th>
                  <th className="border px-4 py-2">Name</th>
                  <th className="border px-4 py-2">Designation</th>
                  <th className="border px-4 py-2">Contact</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.length ? (
                  filteredStaff.map((s, i) => (
                    <tr key={s._id} className="text-center hover:bg-gray-100">
                      <td className="border px-4 py-2">{i + 1}</td>
                      <td className="border px-4 py-2">{s.name}</td>
                      <td className="border px-4 py-2">{s.designation?.designationName}</td>
                      <td className="border px-4 py-2">{s.contactNumber}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-6 text-gray-500">
                      No staff found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

export default NonTeachingStaffReport;
